import { type FC, useEffect, useState } from "react";
import {
    ALLOWED_SPECIAL_CHARS,
    type IIsStrongPasswordResult,
    isStrongPassword,
    PWD_MIN_LENGTH
} from "../../../src/shared";
import { Alert, Box, Collapse, List, ListItem, Typography } from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";

const Status: FC<{ status: boolean; label: string }> = ({ status, label }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center"
            }}
        >
            {status ? <CheckCircle color={"success"}/> : <Cancel color={"error"} />}
            <Typography component={"p"} variant={"caption"} sx={{mx: 1}}>{label}</Typography>
        </Box>
    );
};

export const PasswordCheck: FC<{ password: string }> = ({ password }) => {
    const [passwordCheck, setPasswordCheck] = useState<IIsStrongPasswordResult>(
        {
            isStrongPassword: false,
            minLength: false,
            minOneLowercase: false,
            minOneNumber: false,
            minOneSpecial: false,
            minOneUppercase: false
        }
    );

    useEffect(() => {
        setPasswordCheck(isStrongPassword(password));
    }, [password]);

    return (
        <Alert severity="info">
            <Typography component={"p"} variant="h6">
                Passwortkriterien:
            </Typography>
            <List>
                <ListItem>
                    <Status
                        label={"Mindestens ein Großbuchstabe"}
                        status={passwordCheck.minOneUppercase}
                    />
                </ListItem>
                <ListItem>
                    <Status
                        label={"Mindestens ein Kleinbuchstabe"}
                        status={passwordCheck.minOneLowercase}
                    />
                </ListItem>
                <ListItem>
                    <Status
                        label={"Mindestens eine Zahl"}
                        status={passwordCheck.minOneNumber}
                    />
                </ListItem>
                <ListItem>
                    <Status
                        label={`Mindestens ein Sonderzeichen (${ALLOWED_SPECIAL_CHARS})`}
                        status={passwordCheck.minOneSpecial}
                    />
                </ListItem>
                <ListItem>
                    <Status
                        label={`Mindestens ${PWD_MIN_LENGTH} Zeichen`}
                        status={passwordCheck.minLength}
                    />
                </ListItem>
            </List>
        </Alert>
    );
};

export const AnimatedPasswordCheck: FC<{ show: boolean; password: string }> = ({ show, password }) => (
    <Collapse in={show} timeout={300}>
        <PasswordCheck password={password} />
    </Collapse>
);
